import { useEffect, useState } from 'react';
import type { HTMLAttributes } from 'react';
import { Button, Tooltip, Dropdown, Table, Typography, message } from 'antd';

import { DownOutlined } from '@ant-design/icons';
import { useDeleteMaterialReservationMutation } from '../../redux/api/material';
import { MaterialReservation } from '../../interfaces/MaterialInventory.interface';
import MaterialReservationModal from '../MaterialReservationModal';
import { Link } from 'react-router-dom';
import { MaterialReservationStatus } from '../MaterialReservationStatus';
import { useHasMultipleBuildings } from '../../hooks/useHasMultipleBuildings';
import { useLocationLabel } from '../../utils/locationLabel';
import {
	hasRecordedCuts,
	releaseErrorMessage,
	releaseTooltip,
} from '../../utils/materialReservationRelease';

const { Text } = Typography;

interface Props {
	data: MaterialReservation[];
	recordsTotal: number;
	currentPage: number;
	onChangePage: (page: number) => void;
	isLoading: boolean;
	refetchData: () => void;
	onChange: () => void;
}

const MaterialReservationsTable = (props: Props) => {
	const [dataSource, setDataSource] = useState<MaterialReservation[]>([]);
	const hasMultipleBuildings = useHasMultipleBuildings();
	const formatLocation = useLocationLabel();

	const [deleteMaterialReservation] = useDeleteMaterialReservationMutation();

	const [isMaterialReservationModalOpen, setIsMaterialReservationModalOpen] =
		useState<boolean>(false);
	const [selectedMaterialReservation, setSelectedMaterialReservation] =
		useState<MaterialReservation | null>(null);

	async function handleDeleteMaterialReservation(
		id: string,
		orderLineItemId?: string
	) {
		await deleteMaterialReservation({ id, orderLineItemId })
			.unwrap()
			.then(() => props.refetchData())
			.catch((error) => message.error(releaseErrorMessage(error)));
	}

	async function handleUpdateMaterialReservation(
		reservation: MaterialReservation
	) {
		setSelectedMaterialReservation(reservation);
		setIsMaterialReservationModalOpen(true);
	}

	function handleCloseMaterialReservationModal() {
		setIsMaterialReservationModalOpen(false);
	}

	const tableColumns: any = [
		{
			title: 'Material',
			width: '20%',
			sorter: true,
			key: 'material_length__material__type__display_name',
			render: (record: MaterialReservation) => (
				<Link
					to={`/material/materials/?search=${record?.material_length?.material?.id}`}
				>
					{`${record?.material_length?.material?.type?.display_name} ${record?.material_length?.material?.shape?.display_name}`}
				</Link>
			),
		},
		{
			title: 'Length ID',
			width: '5%',
			sorter: true,
			key: 'material_length__friendly_id',
			render: (record: MaterialReservation) => (
				<Text>{`${record?.material_length?.friendly_id}`}</Text>
			),
		},
		...(hasMultipleBuildings ? [{
			title: 'Building',
			key: 'building',
			width: '8%',
			render: (record: MaterialReservation) => {
				const building = (record?.material_length?.inventory_location as any)?.shelf?.rack?.building;
				return <Text>{building?.name || '—'}</Text>;
			},
		}] : []),
		{
			title: 'Location',
			width: '15%',
			sorter: true,
			key: 'material_length__inventory_location',
			render: (record: MaterialReservation) =>
				record?.material_length?.inventory_location ? (
					<Text>
						{formatLocation(record.material_length.inventory_location.display_name) ||
							record.material_length.inventory_location.code}
					</Text>
				) : (
					<Text type={"secondary"}>No Location Assigned</Text>
				),
		},
		{
			title: 'Size',
			width: '10%',
			render: (record: MaterialReservation) => record.display_size || '',
		},
		{
			title: 'Cut Length',
			width: '5%',
			render: (record: MaterialReservation) =>
				Number(record.material_cut_length),
		},
		{
			title: 'Cut Quantity',
			width: '5%',
			render: (record: MaterialReservation) => record.material_cut_quantity,
		},
		{
			title: 'Reserved Length',
			width: '5%',
			render: (record: MaterialReservation) => Number(record.reserved_length),
		},
		{
			title: 'Line Item',
			width: '15%',
			sorter: true,
			key: 'order_line_item__name',
			render: (record: MaterialReservation) => {
				if (record.order_line_item) {
					return (
						<>
							<Link to={`/orders/${record.order_line_item?.order?.id}/`}>
								{record.order_line_item?.order?.name}
							</Link>{' '}
							<br />
							<Link to={`/orders/line-items/${record.order_line_item.id}/`}>
								{record.order_line_item?.name}
							</Link>
						</>
					);
				}
				return <>{record.reserved_for_description}</>;
			},
		},
		{
			title: 'Cut Status',
			width: '10%',
			sorter: true,
			key: 'material_cut_status',
			render: (record: MaterialReservation) => (
				<MaterialReservationStatus
					reservation={record}
					refetch={() => {
						props.refetchData();
					}}
				/>
			),
		},
		{
			title: 'Actual Cut Qty',
			width: '10%',
			render: (record: MaterialReservation) => {
				return (
					<Text>
						{record.actual_cut_quantity} of {record.material_cut_quantity}
					</Text>
				);
			},
		},
		{
			title: 'Created Time',
			width: '15%',
			key: 'created_time',
			sorter: (
				a: { created_time: string | number | Date },
				b: { created_time: string | number | Date }
			) =>
				new Date(a.created_time).getTime() - new Date(b.created_time).getTime(),
			render: (record: MaterialReservation) => {
				if (!record?.created_time) return <>Unknown</>;

				const date = new Date(record.created_time);

				const formattedDate = date.toLocaleString('en-US', {
					month: 'long', // "March"
					day: 'numeric', // "31"
					year: 'numeric', // "2025"
					hour: 'numeric', // "1"
					minute: '2-digit', // "31"
					second: '2-digit', // "49"
					hour12: true, // "a.m./p.m."
				});

				return <Text>{formattedDate}</Text>;
			},
		},
		{
			title: 'Actions',
			key: 'select',
			width: '10%',
			render: (record: MaterialReservation) => (
				<div>
					<Dropdown
						menu={{
							items: [
								{
									key: '1',
									label: 'Edit',
									disabled: hasRecordedCuts(record),
									onClick: () => handleUpdateMaterialReservation(record),
								},
								{
									key: '2',
									label: (
										<Tooltip title={releaseTooltip(record)}>Release</Tooltip>
									),
									disabled: hasRecordedCuts(record),
									onClick: () =>
										handleDeleteMaterialReservation(
											record.id,
											record.order_line_item?.id
										),
								},
							],
						}}
						trigger={['click']}
						placement="bottomRight"
					>
						<Button
							icon={<DownOutlined />}
							disabled={hasRecordedCuts(record)}
						>
							Actions
						</Button>
					</Dropdown>
				</div>
			),
		},
	];

	useEffect(() => {
		if (props.data) {
			setDataSource(props.data);
		}
	}, [props.data]);

	return (
		<>
			<div
				data-testid="fastlane-material-reservations-table"
				data-ready={!props.isLoading ? 'true' : 'false'}
			>
				<Table
					scroll={{ x: '720px' }}
					columns={tableColumns}
					dataSource={dataSource}
					loading={props.isLoading}
					rowKey="id"
					onRow={(record) => ({
						'data-testid': 'fastlane-material-reservation-row',
						'data-material-reservation-id': record.id,
					} as HTMLAttributes<HTMLElement>)}
					onChange={props.onChange}
					pagination={{
						pageSize: 20,
						current: props.currentPage,
						pageSizeOptions: [],
						total: props.recordsTotal,
						onChange: (page) => {
							props.onChangePage(page);
						},
						defaultPageSize: 20,
						showSizeChanger: false,
					}}
				/>
			</div>

			<MaterialReservationModal
				onClose={handleCloseMaterialReservationModal}
				selectedMaterialReservation={selectedMaterialReservation}
				isMaterialReservationModalOpen={isMaterialReservationModalOpen}
				refetchData={props.refetchData}
			/>
		</>
	);
};

export default MaterialReservationsTable;
